/**
 * The frame clock and the environment probes for the page.
 *
 * `requestAnimationFrame` gives the frame's start time; the change a callback
 * makes is committed some time after it returns, and physically visible later
 * still. So a stimulus drawn in frame N is first OBSERVABLE to JavaScript in
 * frame N+1 — that is what `afterPaint` reports, and it is an upper bound on
 * when the code can know, not a measurement of the display. The gap between
 * this and physical onset is what `docs/beacon-measurement.md` measures with
 * an external reference.
 */

export class FrameClock {
  private raf = 0
  private running = false
  readonly intervals: number[] = []
  private last: number | null = null
  private listeners = new Set<(now: number) => void>()
  private pendingAfterPaint: ((now: number) => void)[] = []

  start(): void {
    if (this.running) return
    this.running = true
    const loop = (now: number) => {
      if (!this.running) return
      if (this.last !== null) this.intervals.push(now - this.last)
      this.last = now
      const after = this.pendingAfterPaint
      this.pendingAfterPaint = []
      for (const cb of after) cb(now)
      for (const l of [...this.listeners]) l(now)
      this.raf = requestAnimationFrame(loop)
    }
    this.raf = requestAnimationFrame(loop)
  }

  stop(): void {
    this.running = false
    cancelAnimationFrame(this.raf)
  }

  onFrame(l: (now: number) => void): () => void {
    this.listeners.add(l)
    return () => {
      this.listeners.delete(l)
    }
  }

  /** Called with the timestamp of the next frame after the current one commits. */
  afterPaint(cb: (now: number) => void): void {
    this.pendingAfterPaint.push(cb)
  }

  stats(): { median: number | null; p95: number | null; max: number | null; count: number; refreshHz: number | null } {
    const s = [...this.intervals].sort((a, b) => a - b)
    const q = (p: number) => (s.length ? s[Math.min(s.length - 1, Math.floor((s.length - 1) * p))]! : null)
    const median = q(0.5)
    return { median, p95: q(0.95), max: s.length ? s[s.length - 1]! : null, count: s.length, refreshHz: median ? Math.round(1000 / median) : null }
  }
}

export const isVisible = () => document.visibilityState === 'visible'
export const isFocused = () => document.hasFocus()

export function deviceContext(inputMethods: Set<string>, refreshHz: number | null) {
  return {
    userAgent: navigator.userAgent,
    platform: (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform,
    devicePixelRatio: window.devicePixelRatio,
    estimatedRefreshHz: refreshHz,
    inputMethods: [...inputMethods].sort(),
    screen: { width: window.screen.width, height: window.screen.height },
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  }
}
